import _ from 'lodash';
import { observable, action } from 'mobx';
import { IDisposer, chunkProcessor } from 'mobx-utils';
import { sleep } from './promises';

type EventQueue<E> = {
    enqueue(...events: E[]): void;
    forward<T>(...events: E[]): (x: T) => T;
    startProcessor: (
        callback: (events: E[]) => void,
        debounce?: number,
        concurrentCount?: number
    ) => IDisposer;
    items: E[];
    waitUntilEmpty: () => Promise<void>;
};

const createQueue = <E>(
    valueFunc: (e: E) => string = _.identity
): EventQueue<E> => {
    const queue = observable([] as E[]);
    const enqueue: EventQueue<E>['enqueue'] = action((...events) => {
        events.forEach(event => {
            !queue.map(valueFunc).includes(valueFunc(event)) &&
                queue.push(event);
        });
    });
    const forward: EventQueue<E>['forward'] = (...events) => {
        return x => {
            enqueue(...events);
            return x;
        };
    };
    const startProcessor: EventQueue<E>['startProcessor'] = (
        callback,
        debounce,
        concurrentCount
    ) => {
        return chunkProcessor(queue, callback, debounce, concurrentCount);
    };
    const waitUntilEmpty = async () => {
        const maxSleepTime = 1.0;
        const step = 0.1;
        let currentSleepTime = 0.1;

        while (queue.length) {
            await sleep(currentSleepTime);
            currentSleepTime = Math.min(maxSleepTime, currentSleepTime + step);
        }
    };

    return {
        get items() {
            return [...queue];
        },
        enqueue,
        forward,
        startProcessor,
        waitUntilEmpty,
    };
};

export { createQueue };
export type { EventQueue };
